import React, { useEffect, useState } from 'react';
import { Radio, Checkbox, Switch, Button, Card, ConfigProvider, Space, Typography, Divider, Alert, Spin, Input, Tag, theme } from 'antd';
import { IconInfoCircle, IconUserCircle, IconLock, IconChevronLeft, IconTruckReturn } from '@tabler/icons-react';
import { message } from '../services/toastMessage';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import dayjs from 'dayjs';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';
import AvailabilityCalendar, { IBookingRange } from './AvailabilityCalendar';
import EmptyState from './EmptyState';
import AccessoryPickerSheet from './AccessoryPickerSheet';
import NearbyOperatorsSection from './NearbyOperatorsSection';
import DeliveryLocationMap from './DeliveryLocationMap';
import PhotoUpload from './PhotoUpload';
import VideoUpload from './VideoUpload';
import PillButton from './PillButton';
import { bookingFlowTheme, tealAccentTheme } from '../theme/appTheme';
import { BookingCheckoutSummaryBar, BookingCheckoutSummaryCard, BookingCheckoutSummarySheet, IBookingDraftLine, IBookingDraftSubLine, IBookingDraftSummary } from './BookingCheckoutSummary';
import { createBooking } from '../services/BookingService';
import { getStripeConfig } from '../services/StripeService';
import { getAvailableOperators, IAvailableOperator, getAvailableAccessories, getAvailableInsurancePolicy, getAvailableServices } from '../services/HeavyDutyMachineryService';
import { IAvailableMachineryAccessory } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { IAvailableHostInsurancePolicy } from '../interfaces/heavyDutyMachinery/IHostInsurancePolicy';
import { IAvailableService } from '../interfaces/heavyDutyMachinery/IService';
import { IBookingAccessory, IBookingServiceLine, IDeliveryCostBreakdownResponse } from '../interfaces/heavyDutyMachinery/IBooking';
import { BookingLinesSummary, IBookingSummaryData, IBookingSummaryLine, IBookingSummarySubLine } from './BookingLinesSummary';
import { useAuth } from '../contextProviders/AuthProvider';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { RENTAL_MODE_OPTIONS } from '../constants/heavyDutyMachinerySpaceOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';
import { estimateDeliveryCost } from '../helpers/deliveryCostHelper';
import { estimateBasePrice, estimateInsuranceCost, computeRentalDays } from '../helpers/insuranceCostHelper';
import { isDailyRentalMode } from '../helpers/rentalPeriodHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { TEAL, TEAL_SOFT_BG, SURFACE_1_SOFT, pillToneStyle } from '../theme/creamyTokens';

const { TextArea } = Input;

type OperationMode = 'DryHire' | 'WetHireHostOperator';
type DeliveryMode = 'SelfPickup' | 'Delivery';
type InsuranceMode = 'None' | 'Platform' | 'Host';

const { Text } = Typography;

interface HeavyDutyMachineryBookingWidgetProps {
  space: IHeavyDutyMachinery;
}

interface IPendingPayment {
  bookingId: string;
  clientSecret: string;
  startAt: string;
  endAt: string;
  rentalMode: BookingRentalMode;
  totalPrice: number;
  basePrice: number;
  // totalPrice/basePrice/operatorCost/deliveryCost/insuranceCost/
  // accessoriesCost/depositAmount below are all denominated in this --
  // see IBooking.hostCurrencyCode's own doc comment.
  hostCurrencyCode: string;
  // What this booking is actually charged in -- see IBooking.guestCurrencyCode.
  guestCurrencyCode: string;
  // totalPrice converted to guestCurrencyCode -- the real charge. See
  // IBooking.guestChargeAmount.
  guestChargeAmount: number;
  operatorCost?: number;
  operatorName?: string;
  deliveryCost?: number;
  // Why deliveryCost is what it is -- see IBooking.deliveryCostBreakdown's own doc comment.
  deliveryCostBreakdown?: IDeliveryCostBreakdownResponse;
  insuranceCost?: number;
  hostInsurancePolicyName?: string;
  accessoriesCost?: number;
  accessories?: IBookingAccessory[];
  // Fase 13: genuinely attached Services only -- filtered down from
  // IBooking.serviceLines at handleSubmit time (that field also carries the
  // operator's own shadow-Service line when one is picked, which stays
  // covered by operatorCost/operatorName above instead, see that field's
  // own doc comment).
  attachedServiceLines?: IBookingServiceLine[];
  // A hold, not a charge -- deliberately kept out of totalPrice/basePrice.
  // See BookingLinesSummary's own copy of this field for how it's displayed.
  depositAmount?: number;
}

const rentalModeLabelKey = (mode: BookingRentalMode): string =>
  RENTAL_MODE_OPTIONS.find((o) => o.value === mode)?.labelKey ?? mode;

/** Turns a delivery cost breakdown (distance/price-per-km/fixed fee/legs --
 * see IDeliveryCostBreakdownResponse's own doc comment) into the "why does
 * this cost what it costs" sub-lines shown under the delivery line, both in
 * the live draft summary (an estimate, built from the guest's own picked
 * pin) and the final payment-step summary (the confirmed booking's own
 * frozen breakdown) -- same shape ({key, label, amount}) either caller
 * needs, so this is the one place the "x2 when ReturnPickupRequested"
 * wording lives instead of duplicated in both. */
const buildDeliverySubLines = (
  breakdown: { distanceKm: number; pricePerKm: number; fixedDispatchFee?: number; legs: number } | undefined,
  hostCurrencyCode: string,
  language: string,
  t: TFunction
): { key: string; label: string; amount: number }[] => {
  if (!breakdown) return [];
  const twoLegs = breakdown.legs === 2;
  const subLines = [
    {
      key: 'distance',
      label: translate(
        t,
        twoLegs
          ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_DISTANCE_SUBLINE_TWO_LEGS
          : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_DISTANCE_SUBLINE,
        { distanceKm: breakdown.distanceKm.toFixed(1), pricePerKm: formatCurrency(breakdown.pricePerKm, hostCurrencyCode, language) }
      ),
      amount: breakdown.distanceKm * 2 * breakdown.legs * breakdown.pricePerKm,
    },
  ];
  if (breakdown.fixedDispatchFee) {
    subLines.push({
      key: 'fixedFee',
      label: translate(
        t,
        twoLegs
          ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_FIXED_FEE_SUBLINE_TWO_LEGS
          : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_FIXED_FEE_SUBLINE
      ),
      amount: breakdown.fixedDispatchFee * breakdown.legs,
    });
  }
  return subLines;
};

// Module-level so every widget instance/re-render reuses the same loadStripe()
// call instead of re-fetching the (non-secret, but still a round trip)
// publishable key and re-initializing Stripe.js on every mount.
let stripePromiseCache: Promise<Stripe | null> | null = null;
const getStripePromise = (): Promise<Stripe | null> => {
  if (!stripePromiseCache) {
    stripePromiseCache = getStripeConfig().then((config) => loadStripe(config.publishableKey));
  }
  return stripePromiseCache;
};

/** The actual card-collection form -- rendered inside <Elements> so useStripe/useElements resolve. */
const BookingPaymentForm: React.FC<{ summary?: IBookingSummaryData; onPaid: () => void; notice?: string | null }> = ({
  summary,
  onPaid,
  notice,
}) => {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // undefined (the prop simply omitted) keeps every existing caller's exact
  // current behavior -- the machine-specific overtime notice below. Pass
  // null explicitly to omit the notice entirely (e.g. a Service booking,
  // which has no engine-hours/overtime concept at all), or a string for a
  // caller-specific one instead.
  const noticeText = notice === undefined ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OVERTIME_CHARGE_NOTICE) : notice;

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setConfirming(true);
    setError(null);

    // redirect: 'if_required' keeps the guest on this page for the common
    // case (a card, confirmed inline) -- only payment methods that
    // genuinely need a redirect (e.g. some bank-redirect methods) leave.
    const { error: confirmError } = await stripe.confirmPayment({ elements, redirect: 'if_required' });

    if (confirmError) {
      setError(confirmError.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PAYMENT_ERROR));
      setConfirming(false);
      return;
    }
    onPaid();
  };

  return (
    <Card bordered={false} style={{ borderRadius: 18 }}>
      <Text strong style={{ fontSize: 16 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PAYMENT_STEP_TITLE)}
      </Text>
      {summary && (
        <div style={{ marginTop: 16 }}>
          <BookingLinesSummary summary={summary} language={i18n.language} />
        </div>
      )}
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <PaymentElement />
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16, borderRadius: 14 }} />}
      {noticeText && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16 }}>
          <IconInfoCircle size={16} style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }} />
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>
            {noticeText}
          </Text>
        </div>
      )}
      <ConfigProvider theme={bookingFlowTheme}>
        <PillButton type="primary" block loading={confirming} onClick={handleConfirm}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_PAY_BUTTON)}
        </PillButton>
      </ConfigProvider>
    </Card>
  );
};

/** Bootstraps Stripe.js (needs the publishable key from the backend) before mounting <Elements>. Exported so ResumePaymentModal (Mis reservas) can reopen this same payment step for an already-created Pending booking, outside this widget's own pick-a-slot flow. summary is optional so that (currently hypothetical) caller isn't forced to have booking details on hand before it can render the payment step at all. */
export const BookingPaymentStep: React.FC<{ clientSecret: string; summary?: IBookingSummaryData; onPaid: () => void; notice?: string | null }> = ({
  clientSecret,
  summary,
  onPaid,
  notice,
}) => {
  const [stripePromise] = useState(() => getStripePromise());

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { variables: { colorPrimary: TEAL, borderRadius: '12px' } } }}
    >
      <BookingPaymentForm summary={summary} onPaid={onPaid} notice={notice} />
    </Elements>
  );
};

// The step "keys" this widget's flow can contain -- 'mode' and 'delivery'
// are only ever present when the space itself supports them (see the
// `steps` array below), so the concrete sequence a given guest walks
// through varies per listing, but the order among the ones that DO apply
// never does. Dates come first (not last, despite "Modalidad, entrega,
// seguro, accesorios, luego fecha y hora" being the original ask) for two
// concrete reasons: (1) AvailabilityCalendar's own `mode` prop needs
// rentalMode already chosen, so the rental-mode pick (Hourly/Overnight/
// DayPass, folded into this same step rather than getting a step of its
// own) has to happen right alongside it anyway; (2) every other step's own
// price line (accessoriesCostEstimate/insuranceCostEstimate/
// deliveryCostEstimate) needs `rentalDays`, which comes from the selected
// range -- with dates last, the sticky summary below would show "pending"
// through almost the whole flow instead of a real running total.
type StepKey = 'dates' | 'mode' | 'delivery' | 'insurance' | 'accessories';

const STEP_TITLE_KEYS: Record<StepKey, string> = {
  dates: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_DATES_TITLE,
  mode: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_MODE_TITLE,
  delivery: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_DELIVERY_TITLE,
  insurance: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_INSURANCE_TITLE,
  accessories: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_ACCESSORIES_TITLE,
};

const HeavyDutyMachineryBookingWidget: React.FC<HeavyDutyMachineryBookingWidgetProps> = ({ space }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const bookableModes: BookingRentalMode[] =
    space.rentalMode === 'Both' ? ['Hourly', 'Overnight'] : space.rentalMode === 'DayPass' ? ['DayPass'] : [space.rentalMode];

  const [rentalMode, setRentalMode] = useState<BookingRentalMode>(bookableModes[0]);
  const [selectedRange, setSelectedRange] = useState<IBookingRange | undefined>(undefined);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Set once CreateBooking succeeds and is waiting on the card payment --
  // switches the widget from "pick a slot" to "pay" for the rest of this visit.
  const [pendingPayment, setPendingPayment] = useState<IPendingPayment | undefined>(undefined);

  // Only worth offering a choice when the space genuinely supports both --
  // one mode alone (the overwhelming majority of listings today) just uses
  // it silently, same reasoning bookableModes.length > 1 already applies to
  // the RentalMode picker below.
  const showOperationModePicker = space.dryHireAvailable && space.wetHireHostOperatorAvailable;
  const [operationMode, setOperationMode] = useState<OperationMode>(
    space.dryHireAvailable ? 'DryHire' : space.wetHireHostOperatorAvailable ? 'WetHireHostOperator' : 'DryHire'
  );
  const [selectedOperatorId, setSelectedOperatorId] = useState<string | undefined>(undefined);
  const [availableOperators, setAvailableOperators] = useState<IAvailableOperator[]>([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [operatorsLoaded, setOperatorsLoaded] = useState(false);

  // Re-fetches on selectedRange change (not just once, unlike before) --
  // MachineryController.GetAvailableOperators reflects each operator's
  // isAvailableForRequestedDates/availableFromDate once startAt/endAt are
  // known, so the list a guest sees genuinely reflects the dates they've
  // picked. Before any dates are picked this still returns every qualified
  // operator with isAvailableForRequestedDates=true (startAt/endAt simply
  // omitted), same as before that field existed. Every qualified operator
  // is always present in the list (never dropped) -- an unavailable one is
  // rendered disabled with its availableFromDate instead.
  useEffect(() => {
    if (operationMode !== 'WetHireHostOperator') return;
    setLoadingOperators(true);
    getAvailableOperators(space.id, selectedRange?.startAt, selectedRange?.endAt)
      .then((operators) => {
        setAvailableOperators(operators);
        // A previously picked operator can become unavailable once dates
        // are known (or change) -- clear the stale pick here rather than
        // leaving it silently selected for a submit CreateBooking would
        // reject anyway once it re-checks the same thing server-side.
        setSelectedOperatorId((prev) => {
          const stillAvailable = operators.find((op) => op.id === prev)?.isAvailableForRequestedDates;
          return prev && !stillAvailable ? undefined : prev;
        });
      })
      .catch(() => setAvailableOperators([]))
      .finally(() => {
        setLoadingOperators(false);
        setOperatorsLoaded(true);
      });
  }, [operationMode, selectedRange, space.id]);

  // Only worth offering when the space genuinely delivers -- same reasoning
  // as showOperationModePicker above.
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>('SelfPickup');
  const [deliveryLatitude, setDeliveryLatitude] = useState<number | undefined>(undefined);
  const [deliveryLongitude, setDeliveryLongitude] = useState<number | undefined>(undefined);
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [deliveryPhotoUrls, setDeliveryPhotoUrls] = useState<string[]>([]);
  const [deliveryVideoUrls, setDeliveryVideoUrls] = useState<string[]>([]);
  // Whether the guest also wants the host to come collect the machine at
  // the end, instead of returning it themselves -- only meaningful (and
  // only ever sent to the backend) while deliveryMode is 'Delivery'.
  const [returnPickupRequested, setReturnPickupRequested] = useState(false);

  // Fase 5: IInsuranceProvider has existed since Fase 0 (MockInsuranceProvider)
  // but was never offered at checkout until now.
  const [insuranceMode, setInsuranceMode] = useState<InsuranceMode>('None');

  // The host's own insurance option for this specific machine, if any (see
  // MachineryController.GetAvailableInsurancePolicy) -- fetched
  // unconditionally, same reasoning as availableAccessories below (nothing
  // to do with operation mode, so no point gating it behind a user pick
  // first). Null once loaded means this machine has no host-provided
  // insurance -- the "Host" radio option is simply never rendered then.
  const [availableInsurancePolicy, setAvailableInsurancePolicy] = useState<IAvailableHostInsurancePolicy | null>(null);

  useEffect(() => {
    getAvailableInsurancePolicy(space.id)
      .then(setAvailableInsurancePolicy)
      .catch(() => setAvailableInsurancePolicy(null));
  }, [space.id]);

  // Fase 11: accessories linked to this space by its own host (see
  // MachineryAccessoryController) -- fetched unconditionally (unlike
  // availableOperators, which only makes sense once WetHireHostOperator is
  // picked), since an accessory has nothing to do with operation mode.
  // Re-fetches on selectedRange change, same reasoning as availableOperators
  // above: MachineryController.GetAvailableAccessories reflects each
  // accessory's own isAvailableForRequestedDates/availableFromDate once
  // startAt/endAt are known (accounting for its Quantity across overlapping
  // bookings), so the list a guest sees genuinely reflects the dates
  // they've picked. Every qualified accessory is always present in the list
  // (never dropped) -- an unavailable one is rendered disabled with its
  // availableFromDate instead, same "shown, not hidden" pattern as operators.
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState<string[]>([]);
  const [availableAccessories, setAvailableAccessories] = useState<IAvailableMachineryAccessory[]>([]);
  const [loadingAccessories, setLoadingAccessories] = useState(true);
  const [accessorySheetOpen, setAccessorySheetOpen] = useState(false);

  useEffect(() => {
    setLoadingAccessories(true);
    getAvailableAccessories(space.id, selectedRange?.startAt, selectedRange?.endAt)
      .then((accessories) => {
        setAvailableAccessories(accessories);
        // A previously picked accessory can become unavailable once dates
        // are known (or change) -- clear the stale pick here rather than
        // leaving it silently selected for a submit CreateBooking would
        // reject anyway once it re-checks the same thing server-side.
        setSelectedAccessoryIds((prev) =>
          prev.filter((id) => accessories.find((a) => a.id === id)?.isAvailableForRequestedDates !== false)
        );
      })
      .catch(() => setAvailableAccessories([]))
      .finally(() => setLoadingAccessories(false));
  }, [selectedRange, space.id]);

  const toggleAccessory = (accessoryId: string) => {
    setSelectedAccessoryIds((prev) => (prev.includes(accessoryId) ? prev.filter((id) => id !== accessoryId) : [...prev, accessoryId]));
    setWaiverAccepted(false);
  };

  // Fase 13: attachable Services (this host's own staff and independent
  // providers alike, whichever is compatible with THIS machine) -- fetched
  // unconditionally like availableAccessories above, same reasoning
  // (nothing to do with operation mode). A bare 404 (ServicesEnabled off)
  // and a genuinely empty list both land here as [] -- either way this
  // section simply doesn't render, same "frontend's job to skip the picker
  // step" convention as accessories/insurance.
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [availableServices, setAvailableServices] = useState<IAvailableService[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);

  useEffect(() => {
    setLoadingServices(true);
    getAvailableServices(space.id, selectedRange?.startAt, selectedRange?.endAt)
      .then((services) => {
        setAvailableServices(services);
        // Same "drop a pick that's since gone stale" reasoning as
        // availableAccessories above -- a Service can lose either
        // isAvailableForRequestedDates (dates changed) or eligibility
        // outright (deposit>0 always blocks it, checked defensively here
        // too even though the list itself should never offer one selectable).
        setSelectedServiceIds((prev) =>
          prev.filter((id) => {
            const svc = services.find((s) => s.id === id);
            return svc && svc.isAvailableForRequestedDates && !(svc.depositAmount && svc.depositAmount > 0);
          })
        );
      })
      .catch(() => setAvailableServices([]))
      .finally(() => setLoadingServices(false));
  }, [selectedRange, space.id]);

  const toggleService = (service: IAvailableService) => {
    if (!service.isAvailableForRequestedDates) return;
    if (service.depositAmount && service.depositAmount > 0) {
      // Explained, not silently blocked -- CreateBooking would reject this
      // outright (see Service.DepositAmount's own doc comment), but a guest
      // seeing a real provider's Service greyed out with no explanation
      // would just be confused about where it went.
      message.info(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_DEPOSIT_BLOCKED_MESSAGE));
      return;
    }
    setSelectedServiceIds((prev) => (prev.includes(service.id) ? prev.filter((id) => id !== service.id) : [...prev, service.id]));
    setWaiverAccepted(false);
  };

  // Rental-days multiplier for the machine's own selected range, once
  // picked -- shared with insuranceCostEstimate's own rentalDays need below.
  const rentalDays = selectedRange ? computeRentalDays(selectedRange.startAt, selectedRange.endAt) : undefined;

  // Per-day rate is known client-side the moment an accessory is fetched,
  // but the TOTAL (TAREA C: PricePerDay * rentalDays, mirroring
  // BookingPriceCalculator's own fix) needs the guest's own selected dates
  // -- same "select your dates" pending state as insuranceCostEstimate
  // below until a range is picked. The backend re-derives the exact same
  // sum from the same live, currently-linked+active accessories at
  // CreateBooking time anyway.
  const accessoriesCostEstimate =
    selectedAccessoryIds.length > 0 && rentalDays
      ? availableAccessories.filter((a) => selectedAccessoryIds.includes(a.id)).reduce((sum, a) => sum + a.pricePerDay, 0) * rentalDays
      : undefined;

  // Live estimate only -- see deliveryCostHelper's own doc comment for why
  // this never needs to be exact, just close: the real charge is always
  // recomputed server-side at CreateBooking time. Full breakdown (not just
  // the total) so the delivery step can show the guest WHY it costs that --
  // fixed dispatch fee, distance x price/km, and whether that's doubled for
  // a return-pickup leg.
  const deliveryCostBreakdown =
    deliveryMode === 'Delivery' &&
    deliveryLatitude != null &&
    deliveryLongitude != null &&
    space.latitude != null &&
    space.longitude != null &&
    space.pricePerKmDelivery != null
      ? estimateDeliveryCost(
          space.latitude, space.longitude, deliveryLatitude, deliveryLongitude,
          space.pricePerKmDelivery, space.fixedDispatchFee, returnPickupRequested
        )
      : undefined;
  const deliveryCostEstimate = deliveryCostBreakdown?.total;

  // Live estimate only, same reasoning as deliveryCostEstimate above -- the
  // real premium is always fetched fresh server-side (IInsuranceProvider.
  // GetQuoteAsync) at CreateBooking time. Needs a selected date/time range
  // to know the rental duration, so it stays undefined ("select your dates"
  // pending state) until one exists.
  const insuranceCostEstimate =
    insuranceMode === 'Platform' && selectedRange
      ? estimateInsuranceCost(
          estimateBasePrice(rentalMode, space.pricePerHour, space.pricePerNight, space.pricePerDayPass, selectedRange.startAt, selectedRange.endAt),
          selectedRange.startAt,
          selectedRange.endAt
        )
      : undefined;

  // Exact, not an estimate -- same reasoning as accessoriesCostEstimate
  // above: availableInsurancePolicy.pricePerDay is already known
  // client-side, and the backend re-derives the exact same
  // PricePerDay * rentalDays from the same live, currently-linked+active
  // policy at CreateBooking time anyway.
  const hostInsuranceCostEstimate =
    insuranceMode === 'Host' && availableInsurancePolicy && selectedRange
      ? availableInsurancePolicy.pricePerDay * computeRentalDays(selectedRange.startAt, selectedRange.endAt)
      : undefined;

  // Base rental price -- same estimateBasePrice helper insuranceCostEstimate
  // already relies on internally, surfaced here as its own line so the
  // sticky summary shows a real running total from step 1 onward instead of
  // starting from an empty breakdown.
  const basePriceEstimate =
    selectedRange != null
      ? estimateBasePrice(rentalMode, space.pricePerHour, space.pricePerNight, space.pricePerDayPass, selectedRange.startAt, selectedRange.endAt)
      : undefined;

  const handleDeliveryModeChange = (mode: DeliveryMode) => {
    setDeliveryMode(mode);
    if (mode !== 'Delivery') setReturnPickupRequested(false);
    setWaiverAccepted(false);
  };

  const handleReturnPickupChange = (checked: boolean) => {
    setReturnPickupRequested(checked);
    setWaiverAccepted(false);
  };

  const handleInsuranceModeChange = (mode: InsuranceMode) => {
    setInsuranceMode(mode);
    setWaiverAccepted(false);
  };

  const minDuration = space.minBookingDurationMinutes ?? space.slotGranularityMinutes;

  const handleRentalModeChange = (mode: BookingRentalMode) => {
    setRentalMode(mode);
    setSelectedRange(undefined);
    setWaiverAccepted(false);
  };

  const handleOperationModeChange = (mode: OperationMode) => {
    setOperationMode(mode);
    setSelectedOperatorId(undefined);
    setWaiverAccepted(false);
  };

  // The calendar owns date/slot selection internally (month grid + per-day
  // slots or check-in/check-out) and only ever reports the resulting
  // startAt/endAt back up here -- any previously accepted waiver no longer
  // applies once the underlying selection changes.
  const handleSelectionChange = (range: IBookingRange | undefined) => {
    setSelectedRange(range);
    setWaiverAccepted(false);
  };

  const operatorSelectionPending = operationMode === 'WetHireHostOperator' && !selectedOperatorId;
  const noOperatorsAvailable = operationMode === 'WetHireHostOperator' && operatorsLoaded && availableOperators.length === 0;
  const deliveryLocationPending = deliveryMode === 'Delivery' && (deliveryLatitude == null || deliveryLongitude == null);
  const canSubmit =
    !!selectedRange && waiverAccepted && !operatorSelectionPending && !noOperatorsAvailable && !deliveryLocationPending;

  // Which of the optional steps actually apply to this listing -- computed
  // once from `space`'s own static flags (never changes across this
  // widget's lifetime), so the step count/sequence stays stable instead of
  // shifting under the guest's feet as async fetches resolve. 'mode' is
  // included whenever wet-hire is possible at all, even when
  // showOperationModePicker itself is false (dry hire not offered) -- the
  // operator picker below still needs its own step in that case, just
  // without the Dry/Wet toggle above it.
  const steps: StepKey[] = [
    'dates',
    ...(space.wetHireHostOperatorAvailable ? (['mode'] as const) : []),
    ...(space.deliveryAvailable ? (['delivery'] as const) : []),
    'insurance',
    'accessories',
  ];

  const [step, setStep] = useState(0);
  const [summarySheetOpen, setSummarySheetOpen] = useState(false);

  const isReviewStep = step >= steps.length;
  const currentStepKey: StepKey | 'review' = isReviewStep ? 'review' : steps[step];
  const totalSteps = steps.length + 1;
  const currentStepNumber = step + 1;

  const canProceedCurrentStep = (): boolean => {
    switch (currentStepKey) {
      case 'dates':
        return !!selectedRange;
      case 'mode':
        return !operatorSelectionPending && !noOperatorsAvailable;
      case 'delivery':
        return !deliveryLocationPending;
      default:
        return true;
    }
  };

  const goNext = () => {
    if (!canProceedCurrentStep()) return;
    setStep((s) => Math.min(s + 1, steps.length));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!selectedRange) return;
    // Anyone can browse a space and pick a slot -- identity is only
    // required for the actual reservation. Same redirectAfterLogin
    // convention ProtectedRoute uses, so login() sends them straight back
    // here (not a generic dashboard) with their selection intact.
    if (!isAuthenticated) {
      message.info(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOGIN_REQUIRED));
      localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      navigate('/login');
      return;
    }
    if (!waiverAccepted) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_WAIVER_REQUIRED));
      return;
    }
    if (deliveryLocationPending) {
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_LOCATION_REQUIRED));
      return;
    }
    setSubmitting(true);
    try {
      const booking = await createBooking({
        heavyDutyMachineryId: Number(space.id),
        startAt: selectedRange.startAt,
        endAt: selectedRange.endAt,
        rentalMode,
        liabilityWaiverAccepted: waiverAccepted,
        operatorId: operationMode === 'WetHireHostOperator' && selectedOperatorId ? Number(selectedOperatorId) : undefined,
        deliveryRequested: deliveryMode === 'Delivery',
        deliveryLatitude: deliveryMode === 'Delivery' ? deliveryLatitude : undefined,
        deliveryLongitude: deliveryMode === 'Delivery' ? deliveryLongitude : undefined,
        returnPickupRequested: deliveryMode === 'Delivery' ? returnPickupRequested : undefined,
        deliveryInstructions: deliveryMode === 'Delivery' && deliveryInstructions.trim() ? deliveryInstructions.trim() : undefined,
        deliveryPhotoUrls: deliveryMode === 'Delivery' && deliveryPhotoUrls.length > 0 ? deliveryPhotoUrls : undefined,
        deliveryVideoUrls: deliveryMode === 'Delivery' && deliveryVideoUrls.length > 0 ? deliveryVideoUrls : undefined,
        insuranceRequested: insuranceMode === 'Platform' || insuranceMode === 'Host',
        insuranceProvider: insuranceMode === 'Platform' || insuranceMode === 'Host' ? insuranceMode : 'None',
        accessoryIds: selectedAccessoryIds.length > 0 ? selectedAccessoryIds.map(Number) : undefined,
        serviceIds: selectedServiceIds.length > 0 ? selectedServiceIds.map(Number) : undefined,
      });
      if (booking.clientSecret) {
        // Confirmed only once Stripe's webhook reports the payment as
        // succeeded -- a separate, out-of-band call from Stripe with no
        // guaranteed timing relative to confirmPayment() resolving here.
        // BookingConfirming (see its own onPaid navigation below) is what
        // actually waits for that and then opens the chat, so there's
        // nothing to show in this widget beyond the payment form itself.
        setPendingPayment({
          bookingId: booking.id,
          clientSecret: booking.clientSecret,
          startAt: booking.startAt,
          endAt: booking.endAt,
          rentalMode,
          totalPrice: booking.totalPrice,
          basePrice: booking.basePrice,
          hostCurrencyCode: booking.hostCurrencyCode,
          guestCurrencyCode: booking.guestCurrencyCode,
          guestChargeAmount: booking.guestChargeAmount,
          operatorCost: booking.operatorCost,
          operatorName: booking.operatorName,
          deliveryCost: booking.deliveryCost,
          deliveryCostBreakdown: booking.deliveryCostBreakdown,
          insuranceCost: booking.insuranceCost,
          hostInsurancePolicyName: booking.hostInsurancePolicyName,
          accessoriesCost: booking.accessoriesCost,
          accessories: booking.accessories,
          // booking.serviceLines also carries the operator's own shadow-
          // Service line whenever one was picked (see IBooking.serviceLines'
          // own doc comment) -- filtered down here to just the ids this
          // guest actually requested as attached Services, so it never
          // duplicates what operatorCost/operatorName above already cover.
          attachedServiceLines: booking.serviceLines.filter((line) => selectedServiceIds.includes(line.serviceId)),
          depositAmount: booking.depositAmount,
        });
      } else {
        // Also covers a reserva diferida submission (Machinery.
        // deferredBookingEnabled) -- CreateBooking never mints a
        // clientSecret for one (see that endpoint's own deferred branch),
        // and its Conversation is already open by the time this response
        // comes back, so landing straight in Messages (rather than a
        // dedicated waiting screen) is enough: the pinned status card there
        // already explains "Presupuesto pendiente" and lets the guest keep
        // talking to the host.
        navigate(`/heavy-duty-machinery/messages?bookingId=${booking.id}`);
      }
    } catch (error: any) {
      const rawMessage = typeof error?.message === 'string' ? error.message : '';
      const isConflict = rawMessage.includes('409');
      // The backend's own outright-refusal message for a Machinery+attached-
      // Service combination that would otherwise mix currencies (see
      // BookingController.CreateBooking's own doc comment on this check) --
      // caught here so the guest sees a clear explanation instead of a
      // generic booking-failed toast.
      const isMixedCurrency = rawMessage.includes('different currencies');
      message.error(
        isMixedCurrency
          ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_MIXED_CURRENCY_ERROR)
          : isConflict
          ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_CONFLICT_ERROR)
          : rawMessage || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ERROR)
      );
      setSelectedRange(undefined);
    } finally {
      setSubmitting(false);
    }
  };

  if (pendingPayment) {
    // Fase 13: one IBookingSummaryLine per real BookingLine on this booking
    // -- base rental, the operator (if any), delivery/insurance/accessories
    // (each still its own line, same breakdown as before this component
    // existed), and finally one per genuinely attached Service. See
    // BookingLinesSummary's own doc comment for why the breakdown section
    // itself only renders once there's more than one of these.
    const summaryLines: IBookingSummaryLine[] = [
      { key: 'base', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_BASE_PRICE_LABEL), amount: pendingPayment.basePrice },
      ...(pendingPayment.operatorCost != null
        ? [
            {
              key: 'operator',
              label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_COST_LABEL),
              providerLabel: pendingPayment.operatorName,
              amount: pendingPayment.operatorCost,
            },
          ]
        : []),
      ...(pendingPayment.deliveryCost != null
        ? [
            {
              key: 'delivery',
              label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_LABEL),
              amount: pendingPayment.deliveryCost,
              subLines: buildDeliverySubLines(
                pendingPayment.deliveryCostBreakdown,
                pendingPayment.hostCurrencyCode,
                i18n.language,
                t
              ) as IBookingSummarySubLine[],
            },
          ]
        : []),
      ...(pendingPayment.insuranceCost != null
        ? [
            {
              key: 'insurance',
              label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL),
              providerLabel: pendingPayment.hostInsurancePolicyName,
              amount: pendingPayment.insuranceCost,
            },
          ]
        : []),
      ...(pendingPayment.accessoriesCost != null
        ? [
            {
              key: 'accessories',
              label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_LABEL),
              providerLabel:
                pendingPayment.accessories && pendingPayment.accessories.length > 0
                  ? pendingPayment.accessories.map((a) => a.name).join(', ')
                  : undefined,
              amount: pendingPayment.accessoriesCost,
            },
          ]
        : []),
      ...(pendingPayment.attachedServiceLines ?? []).map((line) => ({
        key: `service-${line.serviceId}`,
        label: line.title,
        providerLabel: line.providerDisplayName,
        amount: line.amount,
      })),
    ];

    const summaryData: IBookingSummaryData = {
      headerTitle: space.title,
      startAt: pendingPayment.startAt,
      endAt: pendingPayment.endAt,
      rentalMode: pendingPayment.rentalMode,
      totalPrice: pendingPayment.totalPrice,
      hostCurrencyCode: pendingPayment.hostCurrencyCode,
      guestCurrencyCode: pendingPayment.guestCurrencyCode,
      guestChargeAmount: pendingPayment.guestChargeAmount,
      lines: summaryLines,
      depositAmount: pendingPayment.depositAmount,
    };

    return (
      <div>
        <Divider />
        <BookingPaymentStep
          clientSecret={pendingPayment.clientSecret}
          summary={summaryData}
          onPaid={() => navigate(`/heavy-duty-machinery/booking-confirming?bookingId=${pendingPayment.bookingId}`)}
        />
      </div>
    );
  }

  // Only ever includes a line once the guest has actively picked that
  // option (never a "not chosen yet" placeholder) -- an operator/Service
  // line's amount stays undefined ("…") because, unlike accessories/
  // insurance, neither has ever had a client-side cost estimate anywhere in
  // this codebase (both are only ever priced server-side, at CreateBooking
  // time) -- showing a guessed number here risked it silently diverging
  // from BookingPriceCalculator's own real math, so it stays honest about
  // not knowing instead.
  // "200,00 €/día × 2 días" -- spelled out under any line that really is a
  // daily rate times a day count, so a guest can see at a glance whether the
  // days they picked are the days they're being charged for. Only ever shown
  // for the by-days mode; an Hourly/DayPass line isn't priced this way.
  const perDayRateLabel = (ratePerDay: number): string | undefined => {
    if (!isDailyRentalMode(rentalMode) || !rentalDays) return undefined;
    const unit = translate(t, rentalDays === 1 ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAYS_SUFFIX);
    const perDay = translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX);
    return `${formatCurrency(ratePerDay, space.hostCurrencyCode, i18n.language)}/${perDay} × ${rentalDays} ${unit}`;
  };

  const draftLines: IBookingDraftLine[] = [];
  if (basePriceEstimate != null) {
    draftLines.push({
      key: 'base',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_BASE_PRICE_LABEL),
      rateLabel: space.pricePerNight != null ? perDayRateLabel(space.pricePerNight) : undefined,
      amount: basePriceEstimate,
    });
  }
  if (operationMode === 'WetHireHostOperator' && selectedOperatorId) {
    const op = availableOperators.find((o) => o.id === selectedOperatorId);
    draftLines.push({
      key: 'operator',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_COST_LABEL),
      providerLabel: op?.fullName,
      amount: undefined,
    });
  }
  if (deliveryMode === 'Delivery') {
    draftLines.push({
      key: 'delivery',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_LABEL),
      amount: deliveryCostEstimate,
      subLines: buildDeliverySubLines(deliveryCostBreakdown, space.hostCurrencyCode, i18n.language, t) as IBookingDraftSubLine[],
    });
  }
  if (insuranceMode === 'Platform') {
    draftLines.push({ key: 'insurance', label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL), amount: insuranceCostEstimate });
  } else if (insuranceMode === 'Host' && availableInsurancePolicy) {
    draftLines.push({
      key: 'insurance',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL),
      providerLabel: availableInsurancePolicy.name,
      amount: hostInsuranceCostEstimate,
    });
  }
  if (selectedAccessoryIds.length > 0) {
    // Accessories bill per rental day, exactly like the machine itself --
    // the industry norm for attachments (a hammer or a bucket has its own
    // daily rate, it isn't a one-off fee). Spelled out for the same reason
    // the base line is: the multiplication is otherwise invisible.
    const accessoriesPerDay = availableAccessories
      .filter((a) => selectedAccessoryIds.includes(a.id))
      .reduce((sum, a) => sum + a.pricePerDay, 0);
    draftLines.push({
      key: 'accessories',
      label: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_LABEL),
      providerLabel: availableAccessories.filter((a) => selectedAccessoryIds.includes(a.id)).map((a) => a.name).join(', ') || undefined,
      rateLabel: perDayRateLabel(accessoriesPerDay),
      amount: accessoriesCostEstimate,
    });
  }
  selectedServiceIds.forEach((serviceId) => {
    const svc = availableServices.find((s) => s.id === serviceId);
    if (!svc) return;
    draftLines.push({ key: `service-${serviceId}`, label: svc.title, providerLabel: svc.providerDisplayName, amount: undefined });
  });

  const draftSummary: IBookingDraftSummary = {
    headerTitle: space.title,
    selectedRange,
    rentalMode,
    currencyCode: space.hostCurrencyCode,
    lines: draftLines,
  };

  const renderDatesStep = () => (
    <div>
      {bookableModes.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RENTAL_MODE_LABEL)}</Text>
          <br />
          <ConfigProvider theme={bookingFlowTheme}>
            <Radio.Group value={rentalMode} onChange={(e) => handleRentalModeChange(e.target.value)}>
              {bookableModes.map((mode) => (
                <Radio.Button key={mode} value={mode}>
                  {translate(t, rentalModeLabelKey(mode))}
                </Radio.Button>
              ))}
            </Radio.Group>
          </ConfigProvider>
        </div>
      )}
      <AvailabilityCalendar
        spaceId={space.id}
        mode={rentalMode}
        maxAdvanceBookingDays={space.maxAdvanceBookingDays}
        minDurationMinutes={minDuration}
        maxDurationMinutes={space.maxBookingDurationMinutes}
        bufferMinutes={space.bufferMinutes}
        minAdvanceBookingHours={space.minAdvanceBookingHours}
        slotGranularityMinutes={space.slotGranularityMinutes}
        pricePerHour={space.pricePerHour}
        currencyCode={space.hostCurrencyCode}
        onSelectionChange={handleSelectionChange}
      />
    </div>
  );

  const renderModeStep = () => (
    <div>
      {showOperationModePicker && (
        <div style={{ marginBottom: 16 }}>
          <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATION_MODE_LABEL)}</Text>
          <br />
          <ConfigProvider theme={bookingFlowTheme}>
            <Radio.Group value={operationMode} onChange={(e) => handleOperationModeChange(e.target.value)}>
              <Radio.Button value="DryHire">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DRY)}</Radio.Button>
              <Radio.Button value="WetHireHostOperator">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_WET_HOST)}</Radio.Button>
            </Radio.Group>
          </ConfigProvider>
        </div>
      )}

      {/* Operators working near this job, whoever the host is. Shown on this
          step no matter which operation mode is picked -- and, crucially,
          even when there is no mode picker at all, which is the case for a
          machine offered without an operator: today such a guest never sees
          the word "operator" anywhere in checkout and has no idea one can be
          arranged. Renders nothing when the host does not allow outside
          operators, or when nobody compatible is in range (see
          NearbyOperatorsSection).

          'browse' and not 'hire': this rental is not secured yet. Hiring
          somebody before the host has accepted it would leave the guest
          paying a person for a job that may never happen -- exactly the
          tangle that keeping the two bookings apart exists to avoid. The
          sheet says so, and the same component turns into a real hire once
          the booking is confirmed (see BookingConfirming/BookingTimelinePage).

          The origin is the delivery point when there is one, so "nearby"
          means near the SITE rather than near the machine's yard, which is
          a different town the moment transport is involved. */}
      <NearbyOperatorsSection
        spaceId={space.id}
        lat={deliveryMode === 'Delivery' ? deliveryLatitude : undefined}
        lng={deliveryMode === 'Delivery' ? deliveryLongitude : undefined}
        startAt={selectedRange?.startAt}
        endAt={selectedRange?.endAt}
        mode="browse"
        style={{ marginBottom: 16 }}
      />

      {operationMode === 'WetHireHostOperator' && (
        <div>
          {loadingOperators ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin size="small" />
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_LOADING)}
              </Text>
            </div>
          ) : noOperatorsAvailable ? (
            <EmptyState
              icon={<IconUserCircle size={20} />}
              title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_EMPTY_TITLE)}
              description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_EMPTY_DESCRIPTION)}
            />
          ) : (
            <>
              <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_SELECT_LABEL)}</Text>
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={8}>
                {availableOperators.map((op) => {
                  const isSelected = op.id === selectedOperatorId;
                  const isUnavailable = !op.isAvailableForRequestedDates;
                  return (
                    <motion.div key={op.id} whileTap={isUnavailable ? undefined : { scale: 0.98 }}>
                      {/* Reserved (overlapping-dates) operators stay in the
                          list rather than being dropped -- same reduced-
                          opacity "not currently usable, but still shown"
                          treatment MyOperators/MyHostInsurancePolicies/
                          MyMachineryAccessories already use for
                          isActive=false rows, so this reads as the same
                          "disabled" state everywhere in the app. */}
                      <Card
                        bordered
                        hoverable={!isUnavailable}
                        onClick={isUnavailable ? undefined : () => setSelectedOperatorId(op.id)}
                        style={{
                          borderRadius: 14,
                          borderColor: isSelected ? TEAL : undefined,
                          borderWidth: isSelected ? 2 : 1,
                          background: isSelected ? SURFACE_1_SOFT : undefined,
                          opacity: isUnavailable ? 0.6 : 1,
                          cursor: isUnavailable ? 'default' : 'pointer',
                        }}
                        styles={{ body: { padding: '10px 14px' } }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <IconUserCircle size={26} style={{ color: isSelected ? TEAL : undefined, flexShrink: 0 }} />
                            <Text strong={isSelected}>{op.fullName}</Text>
                          </div>
                          {isUnavailable ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                padding: '2px 10px',
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                flexShrink: 0,
                                ...pillToneStyle('warning', token),
                              }}
                            >
                              <IconLock size={12} />
                              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_UNAVAILABLE_LABEL)}
                            </span>
                          ) : (
                            <Text style={{ color: isSelected ? TEAL : undefined, whiteSpace: 'nowrap' }}>
                              {formatCurrency(op.hourlyRate, space.hostCurrencyCode, i18n.language)}
                              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_RATE_SUFFIX)}
                            </Text>
                          )}
                        </div>
                        {isUnavailable && op.availableFromDate && (
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_AVAILABLE_FROM_LABEL, {
                              date: formatFullDate(dayjs(op.availableFromDate), i18n.language),
                            })}
                          </Text>
                        )}
                      </Card>
                    </motion.div>
                  );
                })}
              </Space>
            </>
          )}
        </div>
      )}
    </div>
  );

  const renderDeliveryStep = () => (
    <div>
      <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_MODE_LABEL)}</Text>
      <br />
      <ConfigProvider theme={bookingFlowTheme}>
        <Radio.Group value={deliveryMode} onChange={(e) => handleDeliveryModeChange(e.target.value)}>
          <Radio.Button value="SelfPickup">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SELF_PICKUP_OPTION)}</Radio.Button>
          <Radio.Button value="Delivery">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_OPTION)}</Radio.Button>
        </Radio.Group>
      </ConfigProvider>

      {deliveryMode === 'Delivery' && (
        <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginTop: 16 }} styles={{ body: { padding: 16 } }}>
          <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 10 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_LOCATION_LABEL)}
          </Text>
          <DeliveryLocationMap
            latitude={deliveryLatitude}
            longitude={deliveryLongitude}
            centerLatitude={space.latitude}
            centerLongitude={space.longitude}
            onChange={(lat, lng) => {
              setDeliveryLatitude(lat);
              setDeliveryLongitude(lng);
            }}
            disabled={submitting}
          />

          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 13 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_INSTRUCTIONS_LABEL)}</Text>
            <TextArea
              value={deliveryInstructions}
              onChange={(e) => setDeliveryInstructions(e.target.value)}
              placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_INSTRUCTIONS_PLACEHOLDER)}
              maxLength={1000}
              rows={2}
              disabled={submitting}
              style={{ borderRadius: 12, marginTop: 4 }}
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_PHOTOS_LABEL)}
            </Text>
            <PhotoUpload domain="bookings" value={deliveryPhotoUrls} onChange={(photos) => setDeliveryPhotoUrls(photos)} maxCount={5} disabled={submitting} />
          </div>

          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_VIDEO_LABEL)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_VIDEO_HELP)}
            </Text>
            <VideoUpload domain="bookings" value={deliveryVideoUrls} onChange={(videos) => setDeliveryVideoUrls(videos)} maxCount={1} disabled={submitting} />
          </div>

          {/* Its own bordered row with a Switch, not a bare Checkbox: this
              decides whether the guest has to haul the machine back
              themselves, and it was sitting unlabelled at the bottom of a
              long delivery form in antd's default near-black, easy to scroll
              straight past. Lifting it into a tappable card that visibly
              turns teal when on -- the widget's own accent, same as every
              other picked option here -- makes "did I ask for collection?"
              answerable at a glance. The whole row is the hit target, since
              a 20px switch is a poor one on a phone. */}
          <div
            role="switch"
            aria-checked={returnPickupRequested}
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RETURN_PICKUP_LABEL)}
            onClick={() => !submitting && handleReturnPickupChange(!returnPickupRequested)}
            style={{
              marginTop: 16,
              padding: '14px 16px',
              borderRadius: 16,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 12,
              cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.6 : 1,
              background: returnPickupRequested ? TEAL_SOFT_BG : token.colorBgContainer,
              border: `${returnPickupRequested ? 2 : 1}px solid ${returnPickupRequested ? TEAL : token.colorBorderSecondary}`,
              transition: 'background-color 200ms ease, border-color 200ms ease',
            }}
          >
            <span
              style={{
                display: 'flex',
                flexShrink: 0,
                width: 34,
                height: 34,
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                background: returnPickupRequested ? TEAL : token.colorFillTertiary,
                color: returnPickupRequested ? '#fff' : token.colorTextSecondary,
              }}
            >
              <IconTruckReturn size={18} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong style={{ display: 'block', fontSize: 14 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RETURN_PICKUP_LABEL)}
              </Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_RETURN_PICKUP_HELP)}
              </Text>
            </div>
            <ConfigProvider theme={tealAccentTheme}>
              <Switch
                checked={returnPickupRequested}
                disabled={submitting}
                style={{ flexShrink: 0, marginTop: 2 }}
                // The row above already handles the tap; without this the
                // click would bubble up and toggle twice, cancelling itself.
                onClick={(_checked, e) => e.stopPropagation()}
                onChange={handleReturnPickupChange}
              />
            </ConfigProvider>
          </div>

          <Divider style={{ margin: '16px 0 12px' }} />
          {deliveryCostBreakdown != null && (
            <div style={{ marginBottom: 8 }}>
              {buildDeliverySubLines(deliveryCostBreakdown, space.hostCurrencyCode, i18n.language, t).map((sub) => (
                <div key={sub.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>{sub.label}</Text>
                  <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatCurrency(sub.amount, space.hostCurrencyCode, i18n.language)}</Text>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Text strong style={{ fontSize: 13 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_LABEL)}</Text>
            {deliveryCostEstimate != null ? (
              <Text strong style={{ fontSize: 15, color: TEAL }}>{formatCurrency(deliveryCostEstimate, space.hostCurrencyCode, i18n.language)}</Text>
            ) : space.pricePerKmDelivery == null ? (
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_UNAVAILABLE)}</Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DELIVERY_COST_PENDING)}</Text>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderInsuranceStep = () => (
    <div>
      <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_MODE_LABEL)}</Text>
      <br />
      <ConfigProvider theme={bookingFlowTheme}>
        <Radio.Group value={insuranceMode} onChange={(e) => handleInsuranceModeChange(e.target.value)}>
          <Radio.Button value="None">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_NONE_OPTION)}</Radio.Button>
          <Radio.Button value="Platform">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_PLATFORM_OPTION)}</Radio.Button>
          {availableInsurancePolicy && <Radio.Button value="Host">{availableInsurancePolicy.name}</Radio.Button>}
        </Radio.Group>
      </ConfigProvider>

      {insuranceMode === 'Platform' && (
        <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginTop: 16 }} styles={{ body: { padding: 16 } }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_HELP)}
          </Text>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Text strong style={{ fontSize: 13 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL)}</Text>
            {insuranceCostEstimate != null ? (
              <Text strong style={{ fontSize: 15, color: TEAL }}>{formatCurrency(insuranceCostEstimate, space.hostCurrencyCode, i18n.language)}</Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_PENDING)}</Text>
            )}
          </div>
        </Card>
      )}

      {insuranceMode === 'Host' && availableInsurancePolicy && (
        <Card bordered={false} style={{ borderRadius: 18, background: SURFACE_1_SOFT, marginTop: 16 }} styles={{ body: { padding: 16 } }}>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
            {availableInsurancePolicy.coverageSummary}
          </Text>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Text strong style={{ fontSize: 13 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_LABEL)}</Text>
            {hostInsuranceCostEstimate != null ? (
              <Text strong style={{ fontSize: 15, color: TEAL }}>{formatCurrency(hostInsuranceCostEstimate, space.hostCurrencyCode, i18n.language)}</Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_INSURANCE_COST_PENDING)}</Text>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderAccessoriesStep = () => (
    <div>
      {/* Fase 11: only ever shown once loaded and this space actually has
          linked accessories -- no picker, no empty state, when it has none
          (see this component's own doc comment on why this fetch is
          unconditional, unlike availableOperators). Opens the full-screen
          AccessoryPickerSheet rather than listing chips inline -- an
          inline row here was easy to miss scrolling past insurance/
          delivery on a long mobile page (see that component's own doc
          comment). Once something's selected this collapses to compact
          pills + the running total, with an Edit button back into the
          sheet, instead of re-showing every option inline. */}
      {!loadingAccessories && availableAccessories.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_LABEL)}</Text>
          <div style={{ marginTop: 8 }}>
            {selectedAccessoryIds.length === 0 ? (
              <ConfigProvider theme={bookingFlowTheme}>
                <PillButton onClick={() => setAccessorySheetOpen(true)}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_ADD_BUTTON)}
                </PillButton>
              </ConfigProvider>
            ) : (
              <Card bordered={false} style={{ borderRadius: 16, background: SURFACE_1_SOFT }} styles={{ body: { padding: 12 } }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {availableAccessories
                    .filter((a) => selectedAccessoryIds.includes(a.id))
                    .map((a) => (
                      <span
                        key={a.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: 999,
                          background: '#fff',
                          border: `1px solid ${TEAL}`,
                          fontSize: 12,
                          color: TEAL,
                          fontWeight: 600,
                        }}
                      >
                        {a.name}
                      </span>
                    ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  {accessoriesCostEstimate != null ? (
                    <Text strong style={{ color: TEAL }}>
                      {formatCurrency(accessoriesCostEstimate, space.hostCurrencyCode, i18n.language)}
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_PENDING)}
                    </Text>
                  )}
                  <Button size="small" shape="round" onClick={() => setAccessorySheetOpen(true)}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_EDIT_BUTTON)}
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      <AccessoryPickerSheet
        open={accessorySheetOpen}
        accessories={availableAccessories}
        selectedIds={selectedAccessoryIds}
        onToggle={toggleAccessory}
        onClose={() => setAccessorySheetOpen(false)}
        rentalDays={rentalDays}
      />

      {/* Fase 13: only ever shown once loaded and something compatible
          actually exists -- no picker, no empty state, when it has none
          (same convention as accessories above). Rendered as a plain
          inline Card list (multi-select, same visual language as
          availableOperators' own Cards above) rather than a picker sheet
          -- today there's only ever one attachable ServiceCategory
          ("Operación de Maquinaria"), so the list is short enough that a
          separate sheet would be more chrome than the content needs. */}
      {!loadingServices && availableServices.length > 0 && (
        <div>
          <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICES_LABEL)}</Text>
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }} size={8}>
            {availableServices.map((service) => {
              const isSelected = selectedServiceIds.includes(service.id);
              const requiresStandalone = !!service.depositAmount && service.depositAmount > 0;
              const isUnavailable = !service.isAvailableForRequestedDates;
              const isBlocked = requiresStandalone || isUnavailable;
              const providerKindLabel = service.isIndependentProvider
                ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_PROVIDER_INDEPENDENT)
                : service.isThisHostsOwnStaff
                  ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_PROVIDER_OWN_STAFF)
                  : translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_PROVIDER_OTHER_STAFF);
              const priceLabel =
                service.pricingMode === 'FixedJob'
                  ? formatCurrency(service.fixedJobPrice ?? 0, service.hostCurrencyCode, i18n.language)
                  : `${formatCurrency(service.hourlyRate ?? 0, service.hostCurrencyCode, i18n.language)}${translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_PRICE_UNIT_HOUR)}`;

              return (
                <motion.div key={service.id} whileTap={isBlocked ? undefined : { scale: 0.98 }}>
                  <Card
                    bordered
                    hoverable={!isUnavailable}
                    onClick={() => toggleService(service)}
                    style={{
                      borderRadius: 14,
                      borderColor: isSelected ? TEAL : undefined,
                      borderWidth: isSelected ? 2 : 1,
                      background: isSelected ? SURFACE_1_SOFT : undefined,
                      opacity: isBlocked ? 0.6 : 1,
                      cursor: isUnavailable ? 'default' : 'pointer',
                    }}
                    styles={{ body: { padding: '10px 14px' } }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <Checkbox checked={isSelected} disabled={isBlocked} />
                        <div style={{ minWidth: 0 }}>
                          <Text strong={isSelected} style={{ display: 'block' }}>
                            {service.title}
                          </Text>
                          <Tag
                            color={service.isIndependentProvider ? 'default' : service.isThisHostsOwnStaff ? 'green' : 'gold'}
                            style={{ margin: '2px 0 0', borderRadius: 999 }}
                          >
                            {providerKindLabel}
                          </Tag>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {requiresStandalone ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 10px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              ...pillToneStyle('warning', token),
                            }}
                          >
                            <IconLock size={12} />
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SERVICE_STANDALONE_ONLY_LABEL)}
                          </span>
                        ) : isUnavailable ? (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 10px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 600,
                              ...pillToneStyle('warning', token),
                            }}
                          >
                            <IconLock size={12} />
                            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OPERATOR_UNAVAILABLE_LABEL)}
                          </span>
                        ) : (
                          <Text style={{ color: isSelected ? TEAL : undefined, whiteSpace: 'nowrap' }}>{priceLabel}</Text>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </Space>
        </div>
      )}

      {!loadingAccessories && availableAccessories.length === 0 && !loadingServices && availableServices.length === 0 && (
        <EmptyState
          icon={<IconLock size={20} />}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_EMPTY_DESCRIPTION)}
        />
      )}
    </div>
  );

  const renderReviewStep = () => (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {translate(
          t,
          // Same reason as submitLabelKey: on a deferred listing nothing is
          // charged at this step, so promising a payment here would be a lie.
          space.deferredBookingEnabled
            ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_REVIEW_DESCRIPTION_QUOTE
            : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_REVIEW_DESCRIPTION
        )}
      </Text>
      {isMobile && (
        <div style={{ marginBottom: 20 }}>
          <BookingCheckoutSummaryCard summary={draftSummary} language={i18n.language} />
        </div>
      )}
      <Checkbox checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_WAIVER_LABEL)}
      </Checkbox>
    </div>
  );

  const renderStepBody = () => {
    switch (currentStepKey) {
      case 'dates':
        return renderDatesStep();
      case 'mode':
        return renderModeStep();
      case 'delivery':
        return renderDeliveryStep();
      case 'insurance':
        return renderInsuranceStep();
      case 'accessories':
        return renderAccessoriesStep();
      default:
        return renderReviewStep();
    }
  };

  const stepTitleKey = isReviewStep ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_REVIEW_TITLE : STEP_TITLE_KEYS[currentStepKey as StepKey];

  // On a deferred listing this button does NOT book anything and does NOT
  // charge the card: it sends the host a quote to approve, and payment only
  // happens if they accept (see BookingController.CreateBooking's deferred
  // branch). Saying "Reservar" there promised something the tap didn't do.
  const submitLabelKey = space.deferredBookingEnabled
    ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUBMIT_QUOTE
    : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SUBMIT;

  return (
    <div>
      <Divider />
      <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        <div style={{ flex: 1, minWidth: 0, paddingBottom: isMobile ? 88 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <Button
              type="text"
              size="small"
              icon={<IconChevronLeft size={16} />}
              onClick={goBack}
              disabled={step === 0}
              style={{ paddingLeft: 0 }}
            >
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_BACK)}
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_INDICATOR, { current: currentStepNumber, total: totalSteps })}
            </Text>
          </div>
          <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 16 }}>
            {translate(t, stepTitleKey)}
          </Text>

          {renderStepBody()}

          {!isMobile && (
            <div style={{ marginTop: 32 }}>
              <ConfigProvider theme={bookingFlowTheme}>
                {!isReviewStep ? (
                  <PillButton type="primary" disabled={!canProceedCurrentStep()} onClick={goNext}>
                    {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_NEXT)}
                  </PillButton>
                ) : (
                  <PillButton type="primary" loading={submitting} disabled={!canSubmit} onClick={handleSubmit}>
                    {translate(t, submitLabelKey)}
                  </PillButton>
                )}
              </ConfigProvider>
            </div>
          )}
        </div>

        {!isMobile && (
          <div style={{ width: 320, flexShrink: 0, position: 'sticky', top: 24 }}>
            <BookingCheckoutSummaryCard summary={draftSummary} language={i18n.language} />
          </div>
        )}
      </div>

      {isMobile && (
        <BookingCheckoutSummaryBar
          summary={draftSummary}
          language={i18n.language}
          onViewBreakdown={() => setSummarySheetOpen(true)}
          actionLabel={translate(t, isReviewStep ? submitLabelKey : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STEP_NEXT)}
          actionDisabled={isReviewStep ? !canSubmit : !canProceedCurrentStep()}
          actionLoading={isReviewStep ? submitting : false}
          onAction={isReviewStep ? handleSubmit : goNext}
        />
      )}
      <BookingCheckoutSummarySheet
        open={summarySheetOpen}
        onClose={() => setSummarySheetOpen(false)}
        summary={draftSummary}
        language={i18n.language}
      />
    </div>
  );
};

export default HeavyDutyMachineryBookingWidget;
